import { useEffect, useRef } from 'react';

type UseFocusModeRecordingRestartArgs = {
  isRecording: boolean;
  isFocusModeOn: boolean;
  prevIsFocusModeOn: boolean | undefined;
  asrPlexFeatureEnabled: boolean;
  stopRecording: () => Promise<void>;
  startRecording: () => Promise<void>;
};

export const useFocusModeRecordingRestart = ({
  isRecording,
  isFocusModeOn,
  prevIsFocusModeOn,
  asrPlexFeatureEnabled,
  stopRecording,
  startRecording,
}: UseFocusModeRecordingRestartArgs) => {
  const shouldRestartForFocusModeRef = useRef(false);
  // Token guards against unintended restarts: only a focus-mode-triggered stop sets a pending token,
  // so recording won't auto-restart after unrelated stops (manual stop, errors, etc.).
  const focusModeToggleTokenRef = useRef(0);
  const pendingFocusModeToggleTokenRef = useRef<number | null>(null);

  // If focus mode flips mid-recording and ASR Plex is enabled, stop so we can restart in the new mode.
  useEffect(() => {
    if (!isRecording) return;
    if (prevIsFocusModeOn == null) return;
    if (prevIsFocusModeOn === isFocusModeOn) return;
    if (!asrPlexFeatureEnabled) return;

    shouldRestartForFocusModeRef.current = true;
    focusModeToggleTokenRef.current += 1;
    pendingFocusModeToggleTokenRef.current = focusModeToggleTokenRef.current;
    stopRecording();
  }, [isRecording, isFocusModeOn, prevIsFocusModeOn, stopRecording, asrPlexFeatureEnabled]);

  // Restart after stop completes to mirror manual stop → toggle → start flow.
  useEffect(() => {
    if (!shouldRestartForFocusModeRef.current) return;
    if (isRecording) return;

    if (pendingFocusModeToggleTokenRef.current == null) {
      shouldRestartForFocusModeRef.current = false;
      return;
    }
    shouldRestartForFocusModeRef.current = false;
    pendingFocusModeToggleTokenRef.current = null;
    startRecording();
  }, [isRecording, startRecording]);
};
